import {
  Badge,
  Button,
  ConfigProvider,
  Flex,
  Tooltip,
} from 'antd'
import type { SizeType } from 'antd/es/config-provider/SizeContext'
import classNames from 'classnames'
import React, { useCallback } from 'react'

import s from './radio-buttons.module.css'

export type DefaultValueType = string | number | undefined

export interface RadioButtonOption<ValueType = DefaultValueType> {
  value: ValueType
  label: string
  badge?: number
  disabled?: boolean
}

export type ValueTypeForMultiple<ValueType, Multiple extends boolean> = Multiple extends true ? ValueType[] : ValueType

export interface RadioButtonsProps<ValueType = DefaultValueType, Multiple extends boolean = false> {
  options: RadioButtonOption<ValueType>[]
  value?: ValueTypeForMultiple<ValueType, Multiple>
  onChange?: (value: ValueTypeForMultiple<ValueType, Multiple>) => void
  className?: string
  withTooltip?: boolean
  scrollable?: boolean
  multiple?: Multiple
  size?: SizeType,
  compareFunc?: (a: ValueType, b: ValueType) => boolean
}

export default function RadioButtons<ValueType = DefaultValueType, Multiple extends boolean = false>(
  {
    options,
    value,
    onChange,
    className,
    withTooltip = false,
    scrollable = true,
    multiple = false as Multiple,
    size = 'small',
    compareFunc = (a, b) => a === b,
  }: RadioButtonsProps<ValueType, Multiple>) {

  const handleClick = useCallback((optionValue: ValueType) => {
    if (multiple) {
      if (Array.isArray(value)) {
        const newValue = value.includes(optionValue)
          ? value.filter(v => v !== optionValue)
          : [...value, optionValue]
        onChange?.(newValue as ValueTypeForMultiple<ValueType, Multiple>)
      } else {
        onChange?.([optionValue] as ValueTypeForMultiple<ValueType, Multiple>)
      }
    } else {
      onChange?.(optionValue as ValueTypeForMultiple<ValueType, Multiple>)
    }
  }, [multiple, onChange, value])

  const isSelected = useCallback((optionValue: ValueType) => {
    if (multiple) {
      return Array.isArray(value) && value.some(v => compareFunc(v as ValueType, optionValue))
    }
    return compareFunc(optionValue, value as ValueType)
  }, [multiple, value, compareFunc])

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: 'transparent',
            defaultBorderColor: 'transparent',
            defaultBg: '#f0f4fe',
            defaultHoverBg: 'var(--ant-color-primary)',
            defaultHoverColor: 'var(--ant-color-white)',
            defaultShadow: '0 0 0',
            borderRadiusSM: 32,
          },
        },
      }}
    >
      <Flex
        className={classNames(s.RadioButtons, { [s.Scrollable]: scrollable }, className)}
      >
        {options.map(({ value: optionValue, label, disabled, badge }, idx) => {
          const selected = isSelected(optionValue)
          const button = (
            <Button
              key={idx}
              size={size}
              type={selected ? 'primary' : 'default'}
              onClick={() => handleClick(optionValue)}
              disabled={disabled}
              shape="round"
            >
              {label}
            </Button>
          )

          if (badge && badge > 0) {
            return (
              <Badge key={idx} count={badge} size="small" className={s.BadgeNumber}>
                {button}
              </Badge>
            )
          }

          if (withTooltip) {
            return <Tooltip key={idx} title={label} placement="top">{button}</Tooltip>
          }
          return button
        })}
      </Flex>
    </ConfigProvider>
  )
}
